import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'
import {
  selectMeetingSummaryAgentEditedLexicalState,
  selectMeetingSummaryAgentEditing,
  selectMeetingSummaryAgentEmbeddedAiResults,
  selectMeetingSummaryAgentLoadingInteraction,
  selectMeetingSummaryFindAndReplaceLoading,
  setMeetingSummaryAgentLoadingInteraction,
  setMeetingSummaryEditedContent,
  setMeetingSummaryEditing,
  setMeetingSummaryElements,
} from '../../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { Button } from '../../../../../../../components/Button/Button'
import { MeetingSummaryInteractionType } from '../../../../../../../models/MeetingSummary'
import { parseMeetingSummary } from '../../utils'
import { getHtmlContentFromState } from '../../../MeetingResults/Lexical/utils'
import { MEETING_SUMMARY_NODES } from '../../Content/Components/LexicalNodes/utils'
import { startEditingAiResult, stopEditingAiResult } from '../../../../../../../store/meetingSummary/display'
import { removeEditedAiResults, selectEditedAiResults } from '../../../../../../../store/meetingSummary/aiResults'
import { useOverrideAiResult } from '../../../hooks/api'
import { useMeetingSummary } from '../../hooks'
import { selectAiFeatures } from '../../../../../../../store/meetingSummary/aiFeatures'
import { addToast } from '../../../../../../../store/toast/toastSlice'

export const MeetingSummaryAgentEditButton: React.FC = () => {
  const dispatch = useAppDispatch()

  const overrideAiResult = useOverrideAiResult()
  const { interactWithMeetingSummary, handleMeetingSummaryResultEdited } = useMeetingSummary()

  const editing = useAppSelector(selectMeetingSummaryAgentEditing)
  const loadingInteraction = useAppSelector(selectMeetingSummaryAgentLoadingInteraction)
  const findAndReplaceLoading = useAppSelector(selectMeetingSummaryFindAndReplaceLoading)
  const editedMeetingSummaryAgentLexicalState = useAppSelector(selectMeetingSummaryAgentEditedLexicalState)
  const embeddedAiResults = useAppSelector(selectMeetingSummaryAgentEmbeddedAiResults)
  const editedAiResults = useAppSelector(selectEditedAiResults)
  const aiFeatures = useAppSelector(selectAiFeatures)

  const handleClickEdit = () => {
    dispatch(setMeetingSummaryEditing(true))
    embeddedAiResults
      .map((r) => r.aiResultId)
      .forEach((aiResultId) => {
        dispatch(startEditingAiResult(aiResultId))
      })
  }

  const handleClickSave = async () => {
    dispatch(setMeetingSummaryEditing(false))
    embeddedAiResults
      .map((r) => r.aiResultId)
      .forEach((aiResultId) => {
        dispatch(stopEditingAiResult(aiResultId))
      })
    dispatch(removeEditedAiResults())
    if (!editedMeetingSummaryAgentLexicalState && !editedAiResults.length) {
      return
    }
    await saveEditedMeetingSummary()
  }

  const saveEditedMeetingSummary = async () => {
    dispatch(addToast({ variant: 'info', title: 'Updating…', messages: ['Updating your meeting summary…'] }))
    dispatch(setMeetingSummaryAgentLoadingInteraction(MeetingSummaryInteractionType.EDIT))
    try {
      if (editedMeetingSummaryAgentLexicalState) {
        const newMeetingSummary = getHtmlContentFromState(editedMeetingSummaryAgentLexicalState, MEETING_SUMMARY_NODES)
        dispatch(setMeetingSummaryElements(parseMeetingSummary(newMeetingSummary)))
        dispatch(setMeetingSummaryEditedContent({ editedLexicalState: null, editedElements: [] }))
        await interactWithMeetingSummary({ type: MeetingSummaryInteractionType.EDIT, newMeetingSummary }, false)
      }
      for (const editedAiResult of editedAiResults) {
        const { id: aiResultId, aiFeatureId, result } = editedAiResult
        const aiFeature = aiFeatures.find((f) => f.id === aiFeatureId)
        if (aiFeature) {
          await overrideAiResult(aiResultId, { result }, () => handleMeetingSummaryResultEdited(aiFeature, aiResultId, result, false))
        }
      }
    } finally {
      dispatch(setMeetingSummaryAgentLoadingInteraction(null))
    }
  }

  const handleClickCancel = () => {
    dispatch(setMeetingSummaryEditing(false))
    dispatch(setMeetingSummaryEditedContent({ editedLexicalState: null, editedElements: [] }))
    embeddedAiResults
      .map((r) => r.aiResultId)
      .forEach((aiResultId) => {
        dispatch(stopEditingAiResult(aiResultId))
      })
    dispatch(removeEditedAiResults())
  }

  if (editing) {
    return (
      <div className='d-flex gap-2'>
        <Button className='px-2 py-1 button-medium' variant='primary' onClick={handleClickSave}>
          Save
        </Button>
        <Button className='px-2 py-1 button-medium' variant='secondary' onClick={handleClickCancel}>
          Cancel
        </Button>
      </div>
    )
  }

  return (
    <Button className='px-2 py-1 button-medium' variant='header' onClick={handleClickEdit} loading={!!loadingInteraction || findAndReplaceLoading}>
      <i className='bi bi-pencil-square' />
      Edit
    </Button>
  )
}
